/**
 * LUSTRO mobile/src/utils/heatSourceNames.ts — nazwy źródeł ciepła w tekstach zaleceń
 * „— dotyczy: …". Od 2026-10-09 zalecenia nie budują już tego formatu, ale STARE zapisy z nim
 * zostają — okno zaleceń ostrzega, gdy wskazane źródło zniknęło z lokalu. Nazwy (typ +
 * pomieszczenie, numeracja powtórzeń) muszą być 1:1 z mobilką.
 */
import { resolveHeatSourceType } from '../ds/HeatSourceLabels';
import { ROOM_OPTIONS } from '../components/RecommendationDetails/PgCogDetails';

export interface PickableHeatSource {
  id: string;
  type: string;
  heatCharacter?: string;
  params?: Record<string, unknown>;
}

/** Separator typu i pomieszczenia: „Kocioł gazowy – kuchnia" (półpauza). */
const LOCATION_SEPARATOR = ' – ';
/** Znacznik listy źródeł w tekście zalecenia (format do 2026-10-09). */
const REFS_MARKER = ' — dotyczy: ';
/** Dopisek przy wspólnych źródłach budynku (format do 2026-10-09). */
const BUILDING_SUFFIX = ' (wspólne źródło budynku)';

/** Etykieta w środku zdania: „Kuchnia" → „kuchnia", ale skróty („WC") zostają. */
function inlineLabel(label: string): string {
  if (label.length > 1 && label[1] === label[1].toLowerCase()) {
    return label[0].toLowerCase() + label.slice(1);
  }
  return label;
}

/**
 * Pomieszczenie źródła z params (pole „Lokalizacja", właściciel 2026-09-13 pkt 10) albo null.
 * „Inne" → ręczny wpis (lokalizacjaInna); nieznana wartość → pokazujemy ją dosłownie.
 */
export function heatSourceLocation(params?: Record<string, unknown>): string | null {
  const value = typeof params?.lokalizacja === 'string' ? params.lokalizacja.trim() : '';
  if (!value) return null;
  if (value === 'inne') {
    const other = typeof params?.lokalizacjaInna === 'string' ? params.lokalizacjaInna.trim() : '';
    return other || null;
  }
  const room = ROOM_OPTIONS.find((r) => r.id === value);
  return room ? inlineLabel(room.label) : value;
}

/**
 * Nazwy urządzeń do tekstu zalecenia: skrót typu ze słownika + pomieszczenie, gdy podane
 * („Kocioł gazowy – kuchnia", audyt 2026-09-30 pkt 2). Gdy ta sama nazwa występuje więcej niż
 * raz — numeracja „(1)", „(2)", żeby dało się je rozróżnić w protokole.
 * `withLocation: false` = format sprzed 2026-09-30 (sam typ) — do rozpoznawania starych tekstów.
 * Zwraca mapę id → nazwa w kolejności listy.
 */
export function heatSourceDisplayNames(
  sources: PickableHeatSource[],
  opts: { withLocation?: boolean } = {},
): Record<string, string> {
  const withLocation = opts.withLocation !== false;
  const labels = sources.map((s) => {
    const type = resolveHeatSourceType(s.type) || s.type;
    const location = withLocation ? heatSourceLocation(s.params) : null;
    return location ? `${type}${LOCATION_SEPARATOR}${location}` : type;
  });
  const counts: Record<string, number> = {};
  for (const l of labels) counts[l] = (counts[l] || 0) + 1;
  const seen: Record<string, number> = {};
  const out: Record<string, string> = {};
  sources.forEach((s, i) => {
    const l = labels[i];
    seen[l] = (seen[l] || 0) + 1;
    out[s.id] = counts[l] > 1 ? `${l} (${seen[l]})` : l;
  });
  return out;
}

/**
 * Nazwy źródeł wskazanych w tekście zalecenia („— dotyczy: A, B"), których NIE MA już na liście
 * źródeł (usunięte albo zmieniony typ/pomieszczenie) — audyt 2026-09-30 pkt 6. Tekstu nie
 * przepisujemy (kominiarz mógł go edytować); okno pokazuje tylko ostrzeżenie.
 * Rozpoznaje też nazwy w starym formacie (sam typ), żeby zalecenia zapisane przed dodaniem
 * pomieszczenia do nazw nie były fałszywie oznaczane. Brak znacznika „— dotyczy:" → [].
 */
export function staleHeatSourceRefs(text: string, sources: PickableHeatSource[]): string[] {
  const line = (text || '').split('\n').find((l) => l.includes(REFS_MARKER));
  if (!line) return [];
  let rest = line.slice(line.indexOf(REFS_MARKER) + REFS_MARKER.length).trim();
  if (rest.endsWith(BUILDING_SUFFIX.trim())) rest = rest.slice(0, -BUILDING_SUFFIX.trim().length).trim();
  if (!rest) return [];

  const known = Array.from(
    new Set([
      ...Object.values(heatSourceDisplayNames(sources)),
      ...Object.values(heatSourceDisplayNames(sources, { withLocation: false })),
    ]),
  ).sort((a, b) => b.length - a.length); // najdłuższe najpierw: „Kocioł gazowy – kuchnia" przed „Kocioł gazowy"

  const stale: string[] = [];
  while (rest) {
    const hit = known.find((n) => rest === n || rest.startsWith(`${n}, `));
    if (hit) {
      rest = rest.slice(hit.length).replace(/^,\s*/, '');
      continue;
    }
    const comma = rest.indexOf(', ');
    const token = (comma < 0 ? rest : rest.slice(0, comma)).trim();
    if (token) stale.push(token);
    rest = comma < 0 ? '' : rest.slice(comma + 2);
  }
  return stale;
}
